import { useTheme, Chip, Badge, SvgIcon, ChipProps, Typography, Box, SxProps } from "@mui/material";
import { DisplayZoneValue, IOZoneValue } from "./smartalarm/types";
import { ReactNode } from "react";
import { BootstrapTooltip } from "./smartalarm/PZConfigurator";
import { useEditedColor } from "../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";

export interface IOZoneValueView extends IOZoneValue {
  port: number;
  deleted: boolean;
}

function isIOZoneValueView(zone: IOZoneValueView | DisplayZoneValue): zone is IOZoneValueView {
  return (zone as IOZoneValue).zone !== undefined;
}

type ZoneViewProps = {
  zone: IOZoneValueView | DisplayZoneValue;
  zoneColor?: string;
  icon?: ReactNode;
  deletedLabel?: string;
  titleView?: boolean;
  tooltip?: boolean;
  boardIndex?: number;
  showBoard?: boolean;
  sx?: SxProps;
  edited?: boolean;
};

const ZoneChip = (props: ZoneViewProps & { chipProps?: ChipProps }) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const { background: editedBackground, text: editedText } = useEditedColor();

  const {
    zone,
    zoneColor: color = theme.palette.primary.main,
    icon,
    deletedLabel: _deletedLabel,
    titleView,
    tooltip,
    boardIndex,
    showBoard = true,
    sx,
    chipProps = {},
    edited,
  } = props;
  const { sx: chipSx, ...restChipProps } = chipProps;

  const deleted = zone.deleted;
  const deletedLabel = _deletedLabel || formatMessage({ defaultMessage: "deleted" }).toUpperCase();

  const code = isIOZoneValueView(zone) ? zone.zone : zone.code;

  const board = isIOZoneValueView(zone) ? boardIndex : zone.board;
  const boardLabel = board === 0 ? "master" : `slave${board}`;
  const zoneLabel = [deleted ? `${deletedLabel} | ` : "", code, zone?.name].filter((i) => !!i).join(" ");

  const label = titleView ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        ...(edited ? { backgroundColor: editedBackground, color: editedText } : {}),
        ...(edited || !zone.enabled ? { borderRadius: 4, paddingX: 1 } : {}),
      }}
      className={!zone.enabled ? "disabledrow" : ""}
    >
      <Typography component="span" variant={titleView ? "h6" : "body2"}>
        {zoneLabel}
      </Typography>
      <Typography sx={{ ml: 1 }}>{boardLabel}</Typography>
    </Box>
  ) : (
    <span>{zoneLabel}</span>
  );

  return (
    <Box sx={[{ position: "relative" }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Chip
        variant="outlined"
        icon={
          <Badge
            badgeContent={zone.port === undefined ? "" : `p${zone.port}`}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            sx={[
              {
                "& .MuiBadge-badge": {
                  left: -8,
                  top: 4,
                  border: `2px solid ${theme.palette.background.paper}`,
                  padding: "0 4px",
                  backgroundColor: color,
                  color: theme.palette.getContrastText(color),
                },
              },
            ]}
          >
            <SvgIcon sx={{ color: color }}>{icon}</SvgIcon>
          </Badge>
        }
        label={
          tooltip ? (
            <BootstrapTooltip backgroundcolor={color} title={label}>
              {label}
            </BootstrapTooltip>
          ) : (
            label
          )
        }
        sx={[
          { m: 1 },
          !!titleView && {
            border: "none",
            width: "fit-content",
            minWidth: "fit-content",
          },
          !!edited &&
            !titleView && {
              backgroundColor: editedBackground,
            },
          ...(Array.isArray(chipSx) ? chipSx : [chipSx]),
        ]}
        className={!zone.enabled && !titleView ? "disabledrow" : ""}
        {...restChipProps}
      />
      {!titleView && board !== undefined && showBoard && (
        <Typography
          variant={"caption"}
          sx={[
            {
              position: "absolute",
              color: theme.palette.primary.main,
              bottom: 1,
              right: "50%",
              transform: "translatex(50%)",
              backgroundColor: theme.palette.common.white,
              lineHeight: "14px",
              pl: 1,
              pr: 1,
              fontStyle: "italic",
              borderRadius: theme.spacing(1),
            },
          ]}
        >
          {boardLabel}
        </Typography>
      )}
    </Box>
  );
};

export default ZoneChip;
